import { HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { NotificationService } from '../services/notification.service';

/**
 * HTTP Interceptor for automatic notification handling
 * Shows success/error notifications for API responses
 */
export const NotificationInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<any> => {
  const notificationService = inject(NotificationService);

  // Skip notifications for certain requests
  const skipNotification = req.headers.get('X-Skip-Notification') === 'true';
  const skipSuccessNotification = req.headers.get('X-Skip-Success-Notification') === 'true';
  const skipErrorNotification = req.headers.get('X-Skip-Error-Notification') === 'true';

  return next(req).pipe(
    tap((event: any) => {
      // Handle successful HTTP responses
      if (event instanceof HttpResponse && !skipNotification && !skipSuccessNotification) {
        const response = event.body;
        
        // Only show success notifications for non-GET requests
        // and when the response indicates success
        if (req.method !== 'GET' && response && typeof response === 'object') {
          // Check if response indicates success
          if (response.success === true || response.status === 'success' || event.status >= 200 && event.status < 300) {
            // Show success notification with custom message or default
            const message = response.message || getDefaultSuccessMessage(req.method);
            if (message) {
              notificationService.showSuccess(message);
            }
          }
        }
      }
    }),
    catchError((error: HttpErrorResponse) => {
      // Handle HTTP errors
      if (!skipNotification && !skipErrorNotification) {
        // Don't show notifications for 401 errors (handled by auth interceptor)
        // and don't show error notifications for GET requests
        if (error.status !== 401 && req.method !== 'GET') {
          notificationService.showApiError(error);
        }
      }
      
      // Re-throw the error so it can be handled by components if needed
      throw error;
    })
  );
};

/**
 * Get default success message based on HTTP method
 */
function getDefaultSuccessMessage(method: string): string {
  switch (method.toUpperCase()) {
    case 'POST':
      return 'Created successfully';
    case 'PUT':
    case 'PATCH':
      return 'Updated successfully';
    case 'DELETE':
      return 'Deleted successfully';
    default:
      return '';
  }
}